import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
} from '@mui/material';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import BlockIcon from '@mui/icons-material/Block';

interface SchoolsResponse {
  schools?: Array<{ isActive?: boolean; isBlocked?: boolean }>;
}
interface UsersResponse {
  users?: unknown[];
}

const KPI_CARD = ({
  label,
  value,
  icon,
  gradient,
  loading,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  loading?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      border: '1px solid',
      borderColor: 'divider',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 2 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={64} height={40} sx={{ mt: 0.5 }} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
            {value}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 52,
          height: 52,
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        {icon}
      </Box>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const { data: schoolsData, isLoading: schoolsLoading } = useQuery<SchoolsResponse>(
    'schools',
    () => apiClient.get('/schools').then((r: { data: unknown }) => r.data as SchoolsResponse)
  );
  const { data: usersData, isLoading: usersLoading } = useQuery<UsersResponse>(
    'users',
    () => apiClient.get('/users').then((r: { data: unknown }) => r.data as UsersResponse)
  );

  const schools = schoolsData?.schools ?? [];
  const users = usersData?.users ?? [];
  const activeSchools = schools.filter((s) => s.isActive).length;
  const blockedSchools = schools.filter((s) => s.isBlocked).length;

  const kpiCards = [
    {
      label: 'Total Schools',
      value: schools.length,
      icon: <SchoolIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      loading: schoolsLoading,
    },
    {
      label: 'Active Schools',
      value: activeSchools,
      icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      loading: schoolsLoading,
    },
    {
      label: 'Total Users',
      value: users.length,
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #4f227d 0%, #7b4da8 100%)',
      loading: usersLoading,
    },
    {
      label: 'Blocked Schools',
      value: blockedSchools,
      icon: <BlockIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      loading: schoolsLoading,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Overview of all schools and users in the platform.
      </Typography>

      <Grid container spacing={3}>
        {kpiCards.map((kpi, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <KPI_CARD
              label={kpi.label}
              value={kpi.value}
              icon={kpi.icon}
              gradient={kpi.gradient}
              loading={kpi.loading}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;
